import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComments,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { ThreadMessage } from "@/services/api/agentic-v2";
import {
  invalidateMissionChannel,
  useMissionChannel,
  useMissionChannelQueryClient,
} from "@/services/api/agentic-v2/queries/useMissionChannel";
import { formatRelativeTime } from "../MissionRow";
import { AgentMessageHtml } from "../AgentMessageHtml";
import AgentWorkerAvatar from "@/components/worker/AgentWorkerAvatar";
import { ComposeBar } from "@/components/chat/ComposeBar";

/**
 * Mission channel — informal advisor↔agents conversation, scoped to a mission.
 * Reads/writes ``agenticv2.messages`` keyed by ``mission_id``.
 */
export interface ChannelTabProps {
  missionId: string;
  /** When false, the channel query does not run (inactive tab / background window). */
  enabled?: boolean;
}

export function ChannelTab({ missionId, enabled = true }: ChannelTabProps) {
  const queryClient = useMissionChannelQueryClient();
  const channelQuery = useMissionChannel({ missionId, enabled });
  const messages = channelQuery.data?.messages ?? [];
  const loading = channelQuery.isLoading;
  const error = channelQuery.error;
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState<Error | null>(null);

  const handleSend = async (text: string) => {
    setBusy(true);
    setSendError(null);
    try {
      await AgenticV2API.postMissionMessage(missionId, { text });
      invalidateMissionChannel(queryClient, missionId);
    } catch (err) {
      setSendError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="channel-tab"
      className="flex flex-col h-full min-h-[320px] rounded-xl bg-slate-700 overflow-hidden"
    >
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
        {loading ? (
          <p className="text-xs text-slate-600 text-center pt-8">Loading…</p>
        ) : error || sendError ? (
          <p className="text-xs text-red-300 text-center pt-8">
            {(error ?? sendError)?.message}
          </p>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center h-full text-slate-500">
            <FontAwesomeIcon
              icon={faComments}
              className="text-xl text-slate-600 mb-3"
            />
            <p className="text-sm">No messages yet</p>
            <p className="text-xs text-slate-600 mt-1 max-w-sm">
              Use this channel for informal direction, questions, and thinking
              out loud with the agents on this mission.
            </p>
          </div>
        ) : (
          messages.map((m) => <ChannelMessage key={m._id} message={m} />)
        )}
      </div>

      <ComposeBar
        onSend={handleSend}
        busy={busy}
        placeholder="Message the mission channel…"
        data-testid="channel-compose"
      />
    </div>
  );
}

function ChannelMessage({ message }: { message: ThreadMessage }) {
  const ts = message.created_at;
  if (message.sender_type === "system" || message.content_type === "system_event") {
    const sysContent = message.content as { label?: string; note?: string };
    const label = sysContent.label ?? "System event";
    return (
      <div className="text-xs text-slate-400 py-1">
        <div className="flex items-center gap-2">
          <span>{label}</span>
          {ts && (
            <span className="ml-auto text-slate-500">
              {formatRelativeTime(ts)}
            </span>
          )}
        </div>
        {sysContent.note && (
          <p className="pl-4 mt-0.5 text-slate-300 italic">
            “{sysContent.note}”
          </p>
        )}
      </div>
    );
  }
  if (message.sender_type === "user") {
    const text = (message.content as { text?: string }).text ?? "";
    let renderedContent: React.ReactNode = <p className="leading-relaxed">{text}</p>;

    if (text.startsWith("info_request_response:")) {
      try {
        const jsonPart = text.substring("info_request_response:".length).trim();
        const payload = JSON.parse(jsonPart) as Record<
          string,
          {
            severity: string;
            label: string;
            answer?: string;
            skipped?: boolean;
            skip_reason?: string;
          }
        >;
        const entries = Object.values(payload);
        if (entries.length > 0) {
          if (entries.length === 1) {
            const entry = entries[0];
            if (entry.skipped) {
              renderedContent = (
                <p className="leading-relaxed italic text-slate-300">
                  Skipped{entry.skip_reason ? `: ${entry.skip_reason}` : ""}
                </p>
              );
            } else {
              renderedContent = <p className="leading-relaxed">{entry.answer}</p>;
            }
          } else {
            renderedContent = (
              <div className="space-y-2.5">
                {entries.map((entry, idx) => (
                  <div key={idx} className="space-y-0.5">
                    <p className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">
                      {entry.label}
                    </p>
                    {entry.skipped ? (
                      <p className="leading-relaxed italic text-slate-300">
                        Skipped{entry.skip_reason ? `: ${entry.skip_reason}` : ""}
                      </p>
                    ) : (
                      <p className="leading-relaxed">{entry.answer}</p>
                    )}
                  </div>
                ))}
              </div>
            );
          }
        }
      } catch {
        renderedContent = <p className="leading-relaxed">{text}</p>;
      }
    }

    return (
      <div className="flex justify-end">
        <div className="rounded-xl px-3 py-2.5 text-sm bg-slate-700/60 text-slate-100 max-w-[80%]">
          {renderedContent}
          {ts && (
            <p className="text-[10px] text-slate-400 mt-1 text-right">
              {formatRelativeTime(ts)}
            </p>
          )}
        </div>
      </div>
    );
  }
  const content = message.content as {
    text?: string;
    agent_name?: string | null;
  };
  return (
    <div className="rounded-xl px-3 py-2.5 text-sm bg-indigo-500/[0.08] text-slate-200 min-w-0 w-full max-w-full overflow-hidden">
      <div className="flex items-center justify-between text-[11px] mb-1">
        <div className="flex items-center gap-1.5">
          <AgentWorkerAvatar
            name={content.agent_name ?? "Agent"}
            size={18}
            className="flex-shrink-0"
          />
          <span className="text-slate-300 font-medium">
            {content.agent_name ?? "Agent"}
          </span>
        </div>
        {ts && (
          <span className="text-slate-400">{formatRelativeTime(ts)}</span>
        )}
      </div>
      <AgentMessageHtml html={content.text ?? ""} />
    </div>
  );
}
